// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// Port of the readiness and validation rules in
// packages/sequent-core/src/types/messaging.rs. The server stays
// authoritative; these let the screens explain a blocked control before saving.
import { EApprovalSource, ECredentialName, EDeliveryFeedback, EMessageChannel, EMessagePurpose, EMessagingProvider, EOutOfWindowPolicy, EProviderApproval, EReadinessBlocker, ERecipientKind, EVENT_MESSAGING_CONFIG_VERSION, GENERATED_CREDENTIALS, MESSAGE_CHANNELS, MESSAGE_PURPOSES, UNSUPPORTED_PROVIDERS, } from "@/types/messaging";
export var recipientKind = function (channel) {
    switch (channel) {
        case EMessageChannel.EMAIL:
            return ERecipientKind.EMAIL_ADDRESS;
        case EMessageChannel.SMS:
        case EMessageChannel.WHATSAPP:
        case EMessageChannel.VIBER:
            return ERecipientKind.PHONE_NUMBER;
        case EMessageChannel.MESSENGER:
            return ERecipientKind.PAGE_SCOPED_ID;
    }
};
/** Whether accounts of `provider` can no longer send and must be replaced. */
export var isUnsupportedProvider = function (provider) {
    return UNSUPPORTED_PROVIDERS.includes(provider);
};
/** The channel a provider sends on; `null` for an unsupported provider. */
export var providerChannel = function (provider) {
    switch (provider) {
        case EMessagingProvider.AWS_SES:
        case EMessagingProvider.SMTP:
            return EMessageChannel.EMAIL;
        case EMessagingProvider.AWS_SNS:
            return EMessageChannel.SMS;
        case EMessagingProvider.WHATSAPP_CLOUD_API:
            return EMessageChannel.WHATSAPP;
        case EMessagingProvider.MESSENGER_SEND_API:
            return EMessageChannel.MESSENGER;
        case EMessagingProvider.VIBER_INFOBIP:
            return EMessageChannel.VIBER;
        case EMessagingProvider.HTTP_API:
        case EMessagingProvider.CONSOLE:
            return null;
    }
};
/** The providers a new account of `channel` can use: an adapter per partner. */
export var providersForChannel = function (channel) {
    return Object.values(EMessagingProvider).filter(function (provider) { return providerChannel(provider) === channel; });
};
export var providerCapabilities = function (provider, channel) {
    if (providerChannel(provider) !== channel) {
        return null;
    }
    var both = __spreadArray([], MESSAGE_PURPOSES, true);
    var base = {
        channel: channel,
        recipient: recipientKind(channel),
        purposes: both,
        template_required_for: [],
        delivery_feedback: EDeliveryFeedback.PROVIDER_RECEIPTS,
        reconciliation: false,
        conversation_window_hours: null,
        requires_provider_approval: false,
    };
    switch (provider) {
        case EMessagingProvider.AWS_SES:
        case EMessagingProvider.SMTP:
        case EMessagingProvider.AWS_SNS:
            // SES reports deliveries only through a notification topic: see senderCapabilities.
            return __assign(__assign({}, base), { delivery_feedback: EDeliveryFeedback.UNAVAILABLE });
        case EMessagingProvider.HTTP_API:
        case EMessagingProvider.CONSOLE:
            return null;
        case EMessagingProvider.WHATSAPP_CLOUD_API:
            return __assign(__assign({}, base), { template_required_for: __spreadArray([], both, true), conversation_window_hours: 24, requires_provider_approval: true });
        case EMessagingProvider.MESSENGER_SEND_API:
            return __assign(__assign({}, base), { conversation_window_hours: 24 });
        case EMessagingProvider.VIBER_INFOBIP:
            return __assign(__assign({}, base), { template_required_for: __spreadArray([], both, true), reconciliation: true });
    }
};
/**
 * What an account can do on `channel`. SES reports deliveries only when a
 * notification topic is set; without one, delivery is unavailable, never 0.
 */
export var senderCapabilities = function (sender, channel) {
    var _a;
    var capabilities = providerCapabilities(sender.provider, channel);
    if (capabilities &&
        sender.provider === EMessagingProvider.AWS_SES &&
        ((_a = sender.notification_topic_arn) === null || _a === void 0 ? void 0 : _a.trim())) {
        return __assign(__assign({}, capabilities), { delivery_feedback: EDeliveryFeedback.PROVIDER_RECEIPTS });
    }
    return capabilities;
};
export var accountCapabilities = function (account) {
    return senderCapabilities(account.sender, account.channel);
};
/**
 * Port of `AccountCheck::approval`: the provider approval the check reported,
 * where the provider has an API for it, else the one an administrator entered.
 */
export var accountApproval = function (entered, check) {
    var _a;
    var reported = (_a = check === null || check === void 0 ? void 0 : check.details) === null || _a === void 0 ? void 0 : _a.provider_approval;
    return reported
        ? { approval: reported, source: EApprovalSource.PROVIDER_CHECK }
        : { approval: entered !== null && entered !== void 0 ? entered : null, source: EApprovalSource.ADMIN_ENTERED };
};
/**
 * What identifies a sender to its provider (`AccountSender::identity`): the
 * provider and its identifying ids, trimmed, absent ones left out. Names,
 * labels, API versions and templates are not part of it. Keep in step with
 * the Rust function; `messaging.test.ts` holds the same cases.
 */
export var accountIdentity = function (sender) {
    var fields = (function () {
        var _a;
        switch (sender.provider) {
            case EMessagingProvider.AWS_SES:
                return [
                    ["from_address", sender.from_address],
                    ["region", sender.region],
                ];
            case EMessagingProvider.SMTP:
                return [
                    ["from_address", sender.from_address],
                    ["server_url", sender.server_url],
                ];
            case EMessagingProvider.AWS_SNS:
                return [
                    ["sender_id", sender.sender_id],
                    ["origination_number", sender.origination_number],
                    ["region", sender.region],
                ];
            case EMessagingProvider.WHATSAPP_CLOUD_API:
                return [
                    ["business_account_id", sender.business_account_id],
                    ["phone_number_id", sender.phone_number_id],
                ];
            case EMessagingProvider.MESSENGER_SEND_API:
                return [["page_id", sender.page_id]];
            case EMessagingProvider.VIBER_INFOBIP:
                return [
                    ["base_url", (_a = sender.base_url) === null || _a === void 0 ? void 0 : _a.replace(/\/+$/, "")],
                    ["sender", sender.sender],
                ];
            default:
                return [];
        }
    })();
    var ids = {};
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        var _a = fields_1[_i], name_1 = _a[0], value = _a[1];
        var trimmed = value === null || value === void 0 ? void 0 : value.trim();
        if (trimmed)
            ids[name_1] = trimmed;
    }
    return { provider: sender.provider, ids: ids };
};
var sameIdentity = function (a, b) {
    var keys = function (identity) { return Object.keys(identity.ids).sort(); };
    return (a.provider === b.provider &&
        keys(a).join() === keys(b).join() &&
        keys(a).every(function (key) { return a.ids[key] === b.ids[key]; }));
};
/**
 * Whether a check describes an account whose identity is `current`
 * (`AccountCheck::is_for`). A check that doesn't name its identity was
 * stored before identities were recorded and counts.
 */
export var checkIsFor = function (check, current) {
    var _a;
    var checked = (_a = check === null || check === void 0 ? void 0 : check.details) === null || _a === void 0 ? void 0 : _a.identity;
    return !checked || sameIdentity(checked, current);
};
/**
 * Whether the account's last check was of another identity (an earlier
 * number, Page or sender): it sends nothing until checked as it is now.
 */
export var checkedForAnotherIdentity = function (account) { var _a; return !!((_a = account.status) === null || _a === void 0 ? void 0 : _a.checked_at) && !checkIsFor(account.status, accountIdentity(account.sender)); };
/**
 * Readiness of `purpose` for `language` (any language when null) on an account
 * whose identity is `identity` (null: unknown, the check is taken as the
 * account's). It always comes from the provider's check: no statement of an
 * administrator replaces it. An account of a removed provider is unsupported.
 * A check of another identity readies nothing: the same rule decides at
 * config save and at send time.
 */
export var purposeReadiness = function (provider, capabilities, approval, check, purpose, language, identity) {
    var _a;
    if (isUnsupportedProvider(provider)) {
        return { purpose: purpose, blockers: [EReadinessBlocker.UNSUPPORTED_PROVIDER] };
    }
    if (!capabilities) {
        return { purpose: purpose, blockers: [EReadinessBlocker.UNSUPPORTED_PURPOSE] };
    }
    var blockers = [];
    var unchecked = !!(check === null || check === void 0 ? void 0 : check.checked_at) && !!identity && !checkIsFor(check, identity);
    if (unchecked) {
        blockers.push(EReadinessBlocker.UNCHECKED);
    }
    if (!unchecked && !(check === null || check === void 0 ? void 0 : check.connected)) {
        blockers.push(EReadinessBlocker.NOT_CONNECTED);
    }
    if (!capabilities.purposes.includes(purpose)) {
        blockers.push(EReadinessBlocker.UNSUPPORTED_PURPOSE);
    }
    if (capabilities.requires_provider_approval &&
        accountApproval(approval, check).approval !== EProviderApproval.CONFIRMED) {
        blockers.push(EReadinessBlocker.NEEDS_PROVIDER_APPROVAL);
    }
    if (unchecked) {
        // The rest comes from the check, which describes another identity.
        return { purpose: purpose, blockers: blockers };
    }
    if (!(check === null || check === void 0 ? void 0 : check.production_access)) {
        blockers.push(EReadinessBlocker.NEEDS_PRODUCTION_ACCESS);
    }
    if (capabilities.template_required_for.includes(purpose)) {
        var languages = (_a = check === null || check === void 0 ? void 0 : check.approved_templates) === null || _a === void 0 ? void 0 : _a[purpose];
        var approved = languages
            ? language
                ? languages.includes(language)
                : languages.length > 0
            : false;
        if (!approved) {
            blockers.push(EReadinessBlocker.NEEDS_APPROVED_TEMPLATE);
        }
    }
    var callback = callbackBlocker(capabilities.channel, purpose, callbackConfigured(check));
    if (callback) {
        blockers.push(callback);
    }
    return { purpose: purpose, blockers: blockers };
};
/**
 * Whether callbacks can reach the account: false only when
 * `messaging_callback_status` says the callback address isn't configured.
 */
export var callbackConfigured = function (check) { var _a; return !((_a = check === null || check === void 0 ? void 0 : check.callback_blockers) === null || _a === void 0 ? void 0 : _a.includes(EReadinessBlocker.CALLBACK_NOT_CONFIGURED)); };
/** Puts each account's callback status on its check, as the screens read it. */
export var withCallbackStatus = function (accounts, statuses) {
    if (!statuses)
        return accounts;
    var byAccount = new Map(statuses.map(function (status) { return [status.account_id, status]; }));
    return accounts.map(function (account) {
        var status = byAccount.get(account.id);
        return status && account.status
            ? __assign(__assign({}, account), { status: __assign(__assign({}, account.status), { callback_blockers: status.blockers, callback_reasons: status.reasons }) }) : account;
    });
};
/**
 * Port of `callback_blocker`: Messenger codes need the callback address,
 * because a code goes out only after the voter's Get Started arrives as a
 * callback. Other purposes and channels send without one.
 */
export var callbackBlocker = function (channel, purpose, configured) {
    return channel === EMessageChannel.MESSENGER && purpose === EMessagePurpose.OTP && !configured
        ? EReadinessBlocker.CALLBACK_NOT_CONFIGURED
        : null;
};
/**
 * Credentials a provider needs before it can send. Webhook credentials are not
 * among them: sending works before callbacks are set up.
 */
export var requiredCredentials = function (provider) {
    switch (provider) {
        case EMessagingProvider.WHATSAPP_CLOUD_API:
        case EMessagingProvider.MESSENGER_SEND_API:
            return [ECredentialName.ACCESS_TOKEN];
        case EMessagingProvider.VIBER_INFOBIP:
            return [ECredentialName.API_KEY];
        case EMessagingProvider.SMTP:
            return [ECredentialName.SMTP_PASSWORD];
        case EMessagingProvider.AWS_SES:
        case EMessagingProvider.AWS_SNS:
        case EMessagingProvider.HTTP_API:
        case EMessagingProvider.CONSOLE:
            return [];
    }
};
/** Credentials an account of `provider` keeps; unsupported providers take none. */
export var acceptedCredentials = function (provider) {
    switch (provider) {
        case EMessagingProvider.AWS_SES:
        case EMessagingProvider.AWS_SNS:
            return [ECredentialName.AWS_ACCESS_KEY_ID, ECredentialName.AWS_SECRET_ACCESS_KEY];
        case EMessagingProvider.WHATSAPP_CLOUD_API:
        case EMessagingProvider.MESSENGER_SEND_API:
            return [
                ECredentialName.ACCESS_TOKEN,
                ECredentialName.APP_SECRET,
                ECredentialName.VERIFY_TOKEN,
            ];
        case EMessagingProvider.VIBER_INFOBIP:
            return [
                ECredentialName.API_KEY,
                ECredentialName.REPORT_USERNAME,
                ECredentialName.REPORT_PASSWORD,
            ];
        default:
            return requiredCredentials(provider);
    }
};
/** Credentials an administrator types; the verify token and report credentials are generated. */
export var typedCredentials = function (provider) {
    return acceptedCredentials(provider).filter(function (name) { return !GENERATED_CREDENTIALS.includes(name); });
};
export var isMetaProvider = function (provider) {
    return provider === EMessagingProvider.WHATSAPP_CLOUD_API ||
        provider === EMessagingProvider.MESSENGER_SEND_API;
};
/**
 * Whether the provider posts delivery reports (and, for Messenger, the voter's
 * Get Started) to the account's callback URL. The full URL comes from the
 * backend, which knows the public address of the webhook component.
 */
export var hasWebhook = function (provider) {
    return isMetaProvider(provider) ||
        provider === EMessagingProvider.VIBER_INFOBIP ||
        provider === EMessagingProvider.AWS_SES;
};
export var channelStatisticsKey = function (channel) {
    switch (channel) {
        case EMessageChannel.EMAIL:
            return "num_emails_sent";
        case EMessageChannel.SMS:
            return "num_sms_sent";
        case EMessageChannel.WHATSAPP:
            return "num_whatsapp_sent";
        case EMessageChannel.VIBER:
            return "num_viber_sent";
        case EMessageChannel.MESSENGER:
            return "num_messenger_sent";
    }
};
/** Sender as voters see it: sender name, number or Page name. */
export var accountSenderLabel = function (sender) {
    switch (sender.provider) {
        case EMessagingProvider.AWS_SES:
        case EMessagingProvider.SMTP:
            return sender.from_name || sender.from_address;
        case EMessagingProvider.AWS_SNS:
            return sender.sender_id || sender.origination_number || null;
        case EMessagingProvider.WHATSAPP_CLOUD_API:
            return sender.display_name || sender.display_phone_number;
        case EMessagingProvider.MESSENGER_SEND_API:
            return sender.page_name || sender.page_id;
        case EMessagingProvider.VIBER_INFOBIP:
            return sender.sender;
        case EMessagingProvider.HTTP_API:
        case EMessagingProvider.CONSOLE:
            return null;
    }
};
/** What validation and the public projection need to know about an account. */
export var accountSummary = function (account) {
    var _a, _b, _c, _d;
    return ({
        id: account.id,
        tenant_id: account.tenant_id,
        channel: account.channel,
        provider: account.provider,
        capabilities: accountCapabilities(account),
        provider_approval: (_a = account.provider_approval) !== null && _a !== void 0 ? _a : EProviderApproval.PENDING,
        identity: accountIdentity(account.sender),
        check: (_b = account.status) !== null && _b !== void 0 ? _b : { connected: false, production_access: false },
        public_label: accountSenderLabel(account.sender),
        messenger_page: account.sender.provider === EMessagingProvider.MESSENGER_SEND_API
            ? {
                page_id: account.sender.page_id,
                username: (_c = account.sender.page_username) !== null && _c !== void 0 ? _c : null,
                name: (_d = account.sender.page_name) !== null && _d !== void 0 ? _d : null,
            }
            : null,
    });
};
/**
 * Whether notices may be sent with an approved template outside the
 * conversation window: only on a channel with a window whose notices are
 * otherwise free text.
 */
export var supportsOutOfWindow = function (capabilities) {
    return !!capabilities &&
        capabilities.conversation_window_hours !== null &&
        !capabilities.template_required_for.includes(EMessagePurpose.NOTICE);
};
/** The language code to send to the provider. */
export var bindingProviderLanguage = function (binding) {
    return binding.provider_language || binding.language;
};
/**
 * The approved template for a message on `channel`. The most specific binding
 * wins: the message's key and language, then the key in any language, then the
 * purpose's default in the language, then any default for the purpose.
 */
export var templateFor = function (config, channel, purpose, key, language) {
    var _a, _b, _c, _d, _e, _f;
    var bindings = ((_b = (_a = config.channels.find(function (entry) { return entry.channel === channel; })) === null || _a === void 0 ? void 0 : _a.templates) !== null && _b !== void 0 ? _b : []).filter(function (binding) { return binding.purpose === purpose; });
    var keyed = function (binding) { return !!key && binding.key === key; };
    var isDefault = function (binding) { return !binding.key; };
    var inLanguage = function (binding) {
        return !!language &&
            (binding.language === language || bindingProviderLanguage(binding) === language);
    };
    return ((_f = (_e = (_d = (_c = bindings.find(function (binding) { return keyed(binding) && inLanguage(binding); })) !== null && _c !== void 0 ? _c : bindings.find(keyed)) !== null && _d !== void 0 ? _d : bindings.find(function (binding) { return isDefault(binding) && inLanguage(binding); })) !== null && _e !== void 0 ? _e : bindings.find(isDefault)) !== null && _f !== void 0 ? _f : null);
};
export var emptyEventMessagingConfig = function () { return ({
    version: EVENT_MESSAGING_CONFIG_VERSION,
    channels: [],
    election_channels: {},
}); };
/**
 * How a channel sends notices outside its conversation window. Messenger
 * always uses the approved utility template: there is no "do not send", and a
 * notice never moves to another channel. Other channels have no such choice.
 */
export var outOfWindowPolicy = function (channel) {
    return channel === EMessageChannel.MESSENGER
        ? EOutOfWindowPolicy.UTILITY_MESSAGES
        : EOutOfWindowPolicy.DISABLED;
};
var isRecord = function (value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};
var asArray = function (value) { return (Array.isArray(value) ? value : []); };
var asRecord = function (value) {
    return isRecord(value) ? value : {};
};
/**
 * Reads a stored configuration, filling the defaults serde applies. The
 * removed `notice_fallback` and `reply_text` are dropped, and each channel
 * gets its out-of-window policy.
 */
export var parseEventMessagingConfig = function (raw) {
    var value = raw;
    if (typeof raw === "string") {
        try {
            value = JSON.parse(raw);
        }
        catch (_a) {
            return emptyEventMessagingConfig();
        }
    }
    if (!isRecord(value)) {
        return emptyEventMessagingConfig();
    }
    var channels = asArray(value.channels)
        .filter(isRecord)
        .map(function (channel) {
        var _a;
        return ({
            channel: channel.channel,
            account_id: String((_a = channel.account_id) !== null && _a !== void 0 ? _a : ""),
            purposes: asArray(channel.purposes),
            templates: asArray(channel.templates),
            out_of_window: outOfWindowPolicy(channel.channel),
        });
    });
    return {
        version: typeof value.version === "number" ? value.version : EVENT_MESSAGING_CONFIG_VERSION,
        channels: channels,
        election_channels: asRecord(value.election_channels),
    };
};
/** Channels enabled for `purpose`, optionally restricted to an election, in channel order. */
export var enabledChannels = function (config, purpose, electionId) {
    var restriction = electionId ? config.election_channels[electionId] : undefined;
    var enabled = config.channels
        .filter(function (channel) { return channel.purposes.includes(purpose); })
        .map(function (channel) { return channel.channel; });
    return MESSAGE_CHANNELS.filter(function (channel) { return enabled.includes(channel) && (!restriction || restriction.includes(channel)); });
};
/** Whether a notice template is bound for `channel`: Messenger's utility template. */
export var noticeTemplateBound = function (entry) {
    return entry.templates.some(function (binding) {
        return binding.purpose === EMessagePurpose.NOTICE && !!binding.provider_template.trim();
    });
};
/** Same checks, in the same order, as `EventMessagingConfig::validate`. */
export var validateEventMessagingConfig = function (config, tenantId, accounts, electionIds) {
    var _a, _b, _c, _d;
    var errors = [];
    if (config.version !== EVENT_MESSAGING_CONFIG_VERSION) {
        errors.push({ kind: "UNSUPPORTED_VERSION", version: config.version });
    }
    var accountsById = new Map(accounts.map(function (account) { return [account.id, account]; }));
    var seen = new Set();
    for (var _i = 0, _e = config.channels; _i < _e.length; _i++) {
        var channelConfig = _e[_i];
        var channel = channelConfig.channel;
        if (seen.has(channel)) {
            errors.push({ kind: "DUPLICATE_CHANNEL", channel: channel });
            continue;
        }
        seen.add(channel);
        var account = accountsById.get(channelConfig.account_id);
        if (!account) {
            errors.push({ kind: "UNKNOWN_ACCOUNT", channel: channel, account_id: channelConfig.account_id });
            continue;
        }
        if (account.tenant_id !== tenantId) {
            errors.push({ kind: "ACCOUNT_OF_ANOTHER_TENANT", account_id: account.id });
            continue;
        }
        if (account.channel !== channel) {
            errors.push({ kind: "ACCOUNT_CHANNEL_MISMATCH", channel: channel, account_id: account.id });
            continue;
        }
        for (var _f = 0, _g = channelConfig.purposes; _f < _g.length; _f++) {
            var purpose = _g[_f];
            var readiness = purposeReadiness(account.provider, account.capabilities, account.provider_approval, account.check, purpose, null, account.identity);
            if (readiness.blockers.length) {
                errors.push({
                    kind: "PURPOSE_NOT_READY",
                    channel: channel,
                    purpose: purpose,
                    blockers: readiness.blockers,
                });
            }
        }
        var templatePurposes = (_b = (_a = account.capabilities) === null || _a === void 0 ? void 0 : _a.template_required_for) !== null && _b !== void 0 ? _b : [];
        for (var _h = 0, _j = channelConfig.templates; _h < _j.length; _h++) {
            var binding = _j[_h];
            if (!templatePurposes.includes(binding.purpose)) {
                continue;
            }
            var languages = (_d = (_c = account.check.approved_templates) === null || _c === void 0 ? void 0 : _c[binding.purpose]) !== null && _d !== void 0 ? _d : [];
            var approved = languages.includes(bindingProviderLanguage(binding)) ||
                languages.includes(binding.language);
            if (!approved) {
                errors.push({
                    kind: "TEMPLATE_NOT_APPROVED",
                    channel: channel,
                    purpose: binding.purpose,
                    language: binding.language,
                });
            }
        }
        if (channelConfig.out_of_window !== EOutOfWindowPolicy.DISABLED &&
            !supportsOutOfWindow(account.capabilities)) {
            errors.push({ kind: "OUT_OF_WINDOW_NOT_SUPPORTED", channel: channel });
        }
        else if (channelConfig.out_of_window === EOutOfWindowPolicy.UTILITY_MESSAGES &&
            channelConfig.purposes.includes(EMessagePurpose.NOTICE) &&
            !noticeTemplateBound(channelConfig)) {
            // Outside the window a notice is sent only with the utility template.
            errors.push({ kind: "UTILITY_TEMPLATE_MISSING", channel: channel });
        }
    }
    var enabled = new Set(config.channels.map(function (channel) { return channel.channel; }));
    for (var _k = 0, _l = Object.keys(config.election_channels).sort(); _k < _l.length; _k++) {
        var electionId = _l[_k];
        if (!electionIds.includes(electionId)) {
            errors.push({ kind: "UNKNOWN_ELECTION", election_id: electionId });
            continue;
        }
        for (var _m = 0, _o = config.election_channels[electionId]; _m < _o.length; _m++) {
            var channel = _o[_m];
            if (!enabled.has(channel)) {
                errors.push({
                    kind: "ELECTION_CHANNEL_NOT_ENABLED",
                    election_id: electionId,
                    channel: channel,
                });
            }
        }
    }
    return errors;
};
/**
 * What Keycloak pages may see: channel labels and purposes, never account
 * identifiers. `electionLabels` are the names a voter record may hold for each
 * election; only restricted elections keep theirs.
 */
export var publicProjection = function (config, accounts, electionLabels) {
    var accountsById = new Map(accounts.map(function (account) { return [account.id, account]; }));
    return {
        version: EVENT_MESSAGING_CONFIG_VERSION,
        channels: config.channels
            .filter(function (entry) { return entry.purposes.length > 0; })
            .flatMap(function (entry) {
            var _a, _b;
            var account = accountsById.get(entry.account_id);
            return account
                ? [
                    {
                        channel: entry.channel,
                        purposes: __spreadArray([], entry.purposes, true),
                        sender_label: (_a = account.public_label) !== null && _a !== void 0 ? _a : null,
                        messenger_page: entry.channel === EMessageChannel.MESSENGER
                            ? ((_b = account.messenger_page) !== null && _b !== void 0 ? _b : null)
                            : null,
                    },
                ]
                : [];
        }),
        election_channels: __assign({}, config.election_channels),
        election_labels: Object.fromEntries(Object.entries(electionLabels).filter(function (_a) {
            var id = _a[0];
            return id in config.election_channels;
        })),
    };
};
export var deliverySummary = function (stats, feedback) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return ({
        voters: (_a = stats === null || stats === void 0 ? void 0 : stats.voters) !== null && _a !== void 0 ? _a : null,
        sentLast24h: (_b = stats === null || stats === void 0 ? void 0 : stats.sent_last_24h) !== null && _b !== void 0 ? _b : null,
        queued: (_c = stats === null || stats === void 0 ? void 0 : stats.queued) !== null && _c !== void 0 ? _c : 0,
        accepted: (_d = stats === null || stats === void 0 ? void 0 : stats.accepted) !== null && _d !== void 0 ? _d : 0,
        delivered: feedback === EDeliveryFeedback.UNAVAILABLE ? null : ((_e = stats === null || stats === void 0 ? void 0 : stats.delivered) !== null && _e !== void 0 ? _e : 0),
        failed: (_f = stats === null || stats === void 0 ? void 0 : stats.failed) !== null && _f !== void 0 ? _f : 0,
        unknown: (_g = stats === null || stats === void 0 ? void 0 : stats.unknown) !== null && _g !== void 0 ? _g : 0,
        unresolved: (_h = stats === null || stats === void 0 ? void 0 : stats.unresolved) !== null && _h !== void 0 ? _h : 0,
    });
};
/**
 * Whether the event tab can show deliveries for an account, and why not:
 * without receipts from the provider (`UNAVAILABLE` alone), or because
 * callbacks can't reach the account (`reason`: the backend's reason, such as
 * "Callback address not configured"). Either way delivery reads
 * "Unavailable", never 0.
 */
export var accountDeliveryFeedback = function (account) {
    var _a, _b, _c, _d, _e, _f, _g;
    var feedback = account
        ? ((_b = (_a = accountCapabilities(account)) === null || _a === void 0 ? void 0 : _a.delivery_feedback) !== null && _b !== void 0 ? _b : EDeliveryFeedback.UNAVAILABLE)
        : EDeliveryFeedback.UNAVAILABLE;
    var blocked = (_d = (_c = account === null || account === void 0 ? void 0 : account.status) === null || _c === void 0 ? void 0 : _c.callback_blockers) !== null && _d !== void 0 ? _d : [];
    if (feedback === EDeliveryFeedback.PROVIDER_RECEIPTS && blocked.length) {
        // Reports can't arrive: the backend's reason, or the blocker itself.
        return {
            feedback: EDeliveryFeedback.UNAVAILABLE,
            reason: (_g = (_f = (_e = account === null || account === void 0 ? void 0 : account.status) === null || _e === void 0 ? void 0 : _e.callback_reasons) === null || _f === void 0 ? void 0 : _f[0]) !== null && _g !== void 0 ? _g : String(blocked[0]),
        };
    }
    return { feedback: feedback };
};
/** Whether `account` can no longer send because its provider was removed. */
export var accountUnsupported = function (account) {
    return isUnsupportedProvider(account.provider);
};
